import React from 'react';
import PropTypes from 'prop-types';

import AlertBar from '~components/common/AlertBar';
import { scrollToTop } from '~utils/window';
import getSessionID from '~utils/sessionID';
import { store } from '~state/ReduxWrapper';
import * as Sentry from '@sentry/gatsby';

export function prepareDataForSubmit(data) {
  const cleanedData = {};

  Object.keys(data).forEach((key) => {
    const value = data[key];

    if (value != null && value !== '') {
      cleanedData[key] = value;
    }
  });

  const { sourcing } = store.getState();

  return {
    utm_campaign: sourcing.utm_campaign,
    utm_source: sourcing.utm_source,
    utm_medium: sourcing.utm_medium,
    utm_term: sourcing.utm_term,
    utm_content: sourcing.utm_content,
    email_referrer: sourcing.email_referrer,
    mobile_referrer: sourcing.mobile_referrer,
    source: sourcing.source,
    session_id: getSessionID(),
    embed_url:
      sourcing.embedPageUrl ||
      (typeof window !== 'undefined' && window.location.href),
    ...cleanedData,
  };
}

export function handleSubmitError(error, values, setFormError) {
  scrollToTop();

  if (
    !error ||
    !error.response ||
    error.response.status >= 500 ||
    error.response.status < 400
  ) {
    setFormError({
      error: true,
      message: 'An error occurred. Please try again.',
      messages: [],
      fieldErrors: {},
    });

    // eslint-disable-next-line no-console
    console.warn(error?.response?.data ?? error);
    Sentry.captureException(error);

    return;
  }

  const serverResponse = error.response.data;

  if (
    !serverResponse ||
    !(serverResponse instanceof Object) ||
    Object.keys(serverResponse).length === 0
  ) {
    Sentry.withScope((scope) => {
      scope.setExtra('serverResponse', serverResponse);
      Sentry.captureException(
        new Error('Server validation response was invalid')
      );
    });

    setFormError({
      error: true,
      message: 'Validation error',
      messages: [],
      fieldErrors: {},
    });

    return;
  }

  const errorFields = Object.keys(serverResponse);

  if (
    errorFields.length === 1 &&
    (errorFields[0] === 'detail' || errorFields[0] === 'non_field_errors')
  ) {
    // For non-form errors, the server just returns a "detail" field
    setFormError({
      error: true,
      message: serverResponse.detail || serverResponse.non_field_errors,
      messages: [],
      fieldErrors: {},
    });

    return;
  }

  const nonFieldErrors = [];
  const fieldErrors = {};
  Object.keys(serverResponse).forEach((key) => {
    const errorMessage = serverResponse[key];

    if (values[key] == null) {
      // this error corresponds to a field that's not in the form (maybe a
      // computed field, or a hard-coded field). Put it in a top-level error
      // that's not shown with a specific field
      nonFieldErrors.push({ title: key, content: errorMessage });
    } else {
      // this error corresponds to a specific field of the form
      fieldErrors[key] = errorMessage;
    }
  });

  if (nonFieldErrors.length > 0) {
    setFormError({
      error: true,
      message: 'Validation failed',
      messages: nonFieldErrors,
      fieldErrors,
    });
  } else {
    setFormError({
      error: true,
      message: 'Validation failed. Please correct the marked fields.',
      messages: [],
      fieldErrors,
    });
  }
}

export const defaultErrorState = {
  error: false,
  message: '',
  messages: [],
  fieldErrors: {},
};

export function ValidationErrors({ message, messages }) {
  return (
    <AlertBar alertType='error'>
      {message && <div>{message}</div>}
      {messages &&
        messages.map(({ title, content }) => (
          <div>
            <strong>{title}: </strong>
            {content}
          </div>
        ))}
    </AlertBar>
  );
}

ValidationErrors.propTypes = {
  message: PropTypes.string,
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      content: PropTypes.string,
    })
  ),
};

ValidationErrors.defaultProps = {
  message: 'Please fix the errors below.',
};
